// hooks/usePlayground.js - Multi-year P&L Scenario with React Query
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../../services/api';
import { ENDPOINTS } from '../../services/endpoints';

// Dynamic year calculation based on current date
const getCurrentYear = () => new Date().getFullYear();

const CURRENT_YEAR = getCurrentYear();
const AVAILABLE_YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2];

const plScenarioQueryDefaults = {
  staleTime: 1000 * 60 * 10,    // 10 minutes
  gcTime: 1000 * 60 * 60,       // 1 hour cache
  retry: 2,
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
};

export const usePlayground = (selectedYear) => {
  const queryClient = useQueryClient();
  
  // Settings
  const [averagingMethod, setAveragingMethod] = useState('last12'); // last3, last6, last12
  const [accountingMethod, setAccountingMethod] = useState('Accrual'); // Accrual or Cash
  
  // User adjustments: keyed by "year-accountId-monthNum"
  const [userAdjustments, setUserAdjustments] = useState({});
  
  // Cache for projected data by year
  const [yearDataCache, setYearDataCache] = useState({});

  // This ensures we have the baseline actual data for projections
  const determineDataYear = useCallback(() => {
    // If viewing last year (2025), fetch 2025 actual data
    if (selectedYear < CURRENT_YEAR) {
      return selectedYear;
    }
    
    // If viewing current or future years, check if we're early in the year
    const currentDate = new Date();
    const currentMonthActual = currentDate.getMonth() + 1; // 1-12
    
    // If early in current year (Jan-Jun), use last year's full data
    if (selectedYear === CURRENT_YEAR && currentMonthActual <= 6) {
      return CURRENT_YEAR - 1;
    }
    
    // If mid-year or later in current year, use current year
    if (selectedYear === CURRENT_YEAR) {
      return CURRENT_YEAR;
    }
    
    // If viewing future years, use last year's full data
    return CURRENT_YEAR - 1;
  }, [selectedYear]);

  const yearToFetch = determineDataYear();

  // Fetch data for the determined year
  const {
    data: apiData,
    isLoading,
    isFetching,
    isError,
    error: apiError,
    refetch: refetchQuery,
  } = useQuery({
    queryKey: ['plScenario', yearToFetch, accountingMethod],
    queryFn: async () => {
      const startTime = performance.now();
      
      const [plResponse, datesResponse] = await Promise.all([
        apiService.get(ENDPOINTS.PL_SCENARIO_DATA, {
          year: yearToFetch,
          accounting_method: accountingMethod,
          use_cache: true
        }),
        apiService.get(ENDPOINTS.FINANCE_MAX_DATES, {
          use_cache: false,  // ✅ Force fresh data from BigQuery
          _t: Date.now()     // ✅ Also bust any HTTP caching
        })
      ]);
      
      return {
        ...plResponse,
        max_date_updated: datesResponse?.max_date_updated || null,
        data_year: yearToFetch
      };
    },
    ...plScenarioQueryDefaults,
  });

  // Extract backend data (this is ALWAYS current year data)
  const rawActuals = useMemo(() => {
    if (!apiData?.line_items) return [];
    
    return apiData.line_items.map(item => {
      const actualAmounts = item.actual_amounts || item.actuals || item.monthly_amounts || {};
            
      return {
        account_id: item.account_id,
        account_name: item.account_name,
        fully_qualified_name: item.fully_qualified_name,
        classification: item.classification,
        account_type: item.account_type,
        account_sub_type: item.account_sub_type,
        actuals: actualAmounts,
        data_year: yearToFetch // ✅ ADD THIS LINE
      };
    });
  }, [apiData, yearToFetch]); // ✅ Add yearToFetch to dependencies

  const currentMonth = apiData?.current_month || null;
  
  // ✅ Get lastSynced from the max_date_updated field
  const lastSynced = apiData?.max_date_updated 
    ? new Date(apiData.max_date_updated).toISOString() 
    : null;

  // Generate months array for selected year
  const months = useMemo(() => {
    const monthsArray = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // ✅ Get actual current month from real-time date
    const currentDate = new Date();
    const actualCurrentMonth = currentDate.getMonth() + 1; // 1-12
    const actualCurrentYear = currentDate.getFullYear();
    
    for (let i = 1; i <= 12; i++) {
      let isActual = false;
      let isHistorical = false;
      let isProjected = false;
      
      if (selectedYear < CURRENT_YEAR) {
        // ✅ Viewing 2025: ALL months are actual (historical)
        isActual = true;
        isHistorical = true;
        
      } else if (selectedYear === CURRENT_YEAR) {
        // ✅ Viewing 2026: Check against REAL current month from today's date
        // NOT from the API's currentMonth (which might be from baseline year 2025)
        
        if (yearToFetch === CURRENT_YEAR) {
          // We're using 2026 data, so we have some actual months
          isActual = actualCurrentMonth && i <= actualCurrentMonth;
          isProjected = !isActual;
        } else {
          // We're using 2025 baseline data for ALL of 2026
          // So ALL months are projected, even January
          isActual = false;
          isProjected = true;
        }
        
      } else {
        // ✅ Viewing 2027+: All projected
        isProjected = true;
      }
      
      monthsArray.push({
        month_num: i,
        month_abbr: monthNames[i - 1],
        is_actual: isActual,
        is_historical: isHistorical,
        is_projected: isProjected,
        baseline_year: yearToFetch
      });
    }
    
    return monthsArray;
  }, [selectedYear, yearToFetch]); // ✅ Remove currentMonth dependency

  // Calculate average from actual data for projections (rolling averages)
  const calculateAverage = useCallback((accountData) => {
    if (!accountData) return 0;

    // ✅ Determine how many months of data we have
    let availableMonths = 12; // Default for historical data
    if (yearToFetch === CURRENT_YEAR && currentMonth) {
      availableMonths = currentMonth;
    }

    let monthsToAverage = [];
    
    switch (averagingMethod) {
      case 'last3':
        const startMonth3 = Math.max(1, availableMonths - 2);
        for (let m = startMonth3; m <= availableMonths; m++) {
          monthsToAverage.push(m);
        }
        break;
      
      case 'last6':
        const startMonth6 = Math.max(1, availableMonths - 5);
        for (let m = startMonth6; m <= availableMonths; m++) {
          monthsToAverage.push(m);
        }
        break;
      
      case 'last12':
        for (let m = 1; m <= availableMonths; m++) {
          monthsToAverage.push(m);
        }
        break;
      
      default:
        for (let m = 1; m <= availableMonths; m++) {
          monthsToAverage.push(m);
        }
    }

    if (monthsToAverage.length === 0) return 0;

    const sum = monthsToAverage.reduce((acc, month) => {
      return acc + (accountData[month] || 0);
    }, 0);

    return sum / monthsToAverage.length;
  }, [averagingMethod, currentMonth, yearToFetch]); // ✅ Add yearToFetch

  // Generate projected line items for selected year
  const projectedLineItems = useMemo(() => {
    const cacheKey = `${selectedYear}-${averagingMethod}-${accountingMethod}-${yearToFetch}`; // ✅ Add yearToFetch
    if (yearDataCache[cacheKey] && Object.keys(userAdjustments).length === 0) {
      return yearDataCache[cacheKey];
    }

    const result = rawActuals.map(item => {
      const projected_amounts = {};

      if (selectedYear < CURRENT_YEAR) {
        // ✅ VIEWING 2025 (Historical Year)
        // Show ALL actual data, no projections
        for (let m = 1; m <= 12; m++) {
          const actualValue = item.actuals[m] || item.actuals[m.toString()] || 0;
          projected_amounts[m] = actualValue;
        }
        
      } else if (selectedYear === CURRENT_YEAR && yearToFetch === CURRENT_YEAR) {
        // ✅ VIEWING 2026 with 2026 data (mid-year)
        // Use actuals for past months, project future
        for (let m = 1; m <= 12; m++) {
          const adjustmentKey = `${selectedYear}-${item.account_id}-${m}`;
          
          if (currentMonth && m <= currentMonth) {
            // Past month: use actual
            const actualValue = item.actuals[m] || item.actuals[m.toString()] || 0;
            projected_amounts[m] = actualValue;
          } else if (userAdjustments[adjustmentKey] !== undefined) {
            // Future month with adjustment
            projected_amounts[m] = userAdjustments[adjustmentKey];
          } else {
            // Future month: project
            projected_amounts[m] = calculateAverage(item.actuals);
          }
        }
        
      } else {
        // ✅ VIEWING 2026 (early year) OR 2027+ 
        // Using 2025 full year data for projections
        let monthsToAverage = [];
        let availableMonths = 12; // Full year for historical baseline
        
        if (yearToFetch === CURRENT_YEAR && currentMonth) {
          availableMonths = currentMonth;
        }
        
        switch (averagingMethod) {
          case 'last3':
            const startMonth3 = Math.max(1, availableMonths - 2);
            for (let m = startMonth3; m <= availableMonths; m++) {
              monthsToAverage.push(m);
            }
            break;
          
          case 'last6':
            const startMonth6 = Math.max(1, availableMonths - 5);
            for (let m = startMonth6; m <= availableMonths; m++) {
              monthsToAverage.push(m);
            }
            break;
          
          case 'last12':
            for (let m = 1; m <= availableMonths; m++) {
              monthsToAverage.push(m);
            }
            break;
          
          default:
            for (let m = 1; m <= availableMonths; m++) {
              monthsToAverage.push(m);
            }
        }

        let futureYearAverage = 0;
        if (monthsToAverage.length > 0) {
          const sum = monthsToAverage.reduce((acc, m) => {
            const actualValue = item.actuals[m] || item.actuals[m.toString()] || 0;
            return acc + actualValue;
          }, 0);
          futureYearAverage = sum / monthsToAverage.length;
        }

        for (let m = 1; m <= 12; m++) {
          const adjustmentKey = `${selectedYear}-${item.account_id}-${m}`;
          
          if (userAdjustments[adjustmentKey] !== undefined) {
            projected_amounts[m] = userAdjustments[adjustmentKey];
          } else {
            projected_amounts[m] = futureYearAverage;
          }
        }
      }

      return {
        ...item,
        projected_amounts,
        is_historical: selectedYear < CURRENT_YEAR, // ✅ ADD THIS
        is_editable: selectedYear >= CURRENT_YEAR,  // ✅ ADD THIS
        baseline_year: yearToFetch                   // ✅ ADD THIS
      };
    });

    if (Object.keys(userAdjustments).length === 0) {
      setYearDataCache(prev => ({
        ...prev,
        [cacheKey]: result
      }));
    }

    return result;
  }, [rawActuals, selectedYear, averagingMethod, accountingMethod, currentMonth, calculateAverage, userAdjustments, yearDataCache, yearToFetch]); // ✅ Add yearToFetch
  
  // Update adjustment for a single month
  const updateAdjustment = useCallback((accountId, monthNum, value, year = selectedYear) => {
    const key = `${year}-${accountId}-${monthNum}`;
    setUserAdjustments(prev => ({
      ...prev,
      [key]: value
    }));
  }, [selectedYear]);

  // Add this new function after updateAdjustment
  const setAllAdjustments = useCallback((adjustments) => {
    // Replace all adjustments at once
    setUserAdjustments(adjustments);
    setYearDataCache({}); // Clear cache
  }, []);


  // Apply adjustment to future months/years
  const applyToAllFuture = useCallback((accountId, monthNum, value, year, scope) => {
    setUserAdjustments(prev => {
      const newAdjustments = { ...prev };

      if (scope === 'same-year') {
        // Apply to all future months in the same year
        for (let m = monthNum; m <= 12; m++) {
          const key = `${year}-${accountId}-${m}`;
          newAdjustments[key] = value;
        }
      } else if (scope === 'all-years') {
        // Apply to future months in current year
        for (let m = monthNum; m <= 12; m++) {
          const key = `${year}-${accountId}-${m}`;
          newAdjustments[key] = value;
        }
        
        // Apply to all months in future years
        const futureYears = AVAILABLE_YEARS.filter(y => y > year);
        futureYears.forEach(futureYear => {
          for (let m = 1; m <= 12; m++) {
            const key = `${futureYear}-${accountId}-${m}`;
            newAdjustments[key] = value;
          }
        });
      }

      return newAdjustments;
    });
  }, []);

  // Clear all adjustments
  const clearAllAdjustments = useCallback(() => {
    setUserAdjustments({});
    setYearDataCache({}); // Clear cache when adjustments are cleared
  }, []);

  // Clear adjustments when accounting method changes
  useEffect(() => {
    setUserAdjustments({});
    setYearDataCache({});
  }, [accountingMethod]);

  // Calculate totals for the selected year
  const totals = useMemo(() => {
    const monthlyTotals = {};
    
    for (let m = 1; m <= 12; m++) {
      monthlyTotals[m] = projectedLineItems.reduce((sum, item) => {
        return sum + (item.projected_amounts[m] || 0);
      }, 0);
    }

    return monthlyTotals;
  }, [projectedLineItems]);

  // ✅ Custom refresh function that clears cache
  const refresh = useCallback(async () => {
    queryClient.removeQueries({ 
      queryKey: ['plScenario', yearToFetch, accountingMethod], // ✅ Changed from CURRENT_YEAR
      exact: true 
    });
    
    return await refetchQuery();
  }, [queryClient, accountingMethod, yearToFetch, refetchQuery]); // ✅ Add yearToFetch

  // Format error message
  const error = isError ? (apiError?.message || 'Failed to load P&L scenario data') : null;

  return {
    // Data
    lineItems: projectedLineItems,
    months,
    currentMonth,
    
    // Multi-year support
    currentYear: CURRENT_YEAR,
    availableYears: AVAILABLE_YEARS,
    baselineYear: yearToFetch, // ✅ ADD THIS - expose which year is being used
    
    // State
    loading: isLoading,
    refreshing: isFetching,
    error,
    isError,
    lastSynced, // ✅ Now properly fetched from FINANCE_MAX_DATES
    
    // Settings
    averagingMethod,
    setAveragingMethod,
    accountingMethod,
    setAccountingMethod,
    
    // Adjustments
    userAdjustments,
    updateAdjustment,
    applyToAllFuture,
    clearAllAdjustments,
    setAllAdjustments,
    
    // Calculated
    totals,
    
    // Actions
    refetch: refresh, // ✅ Use custom refresh that clears cache
  };
};